import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, Modal } from 'react-bootstrap';

//Components
import withLocalization from '~/hoc/withLocalization';
import GenericList from '~/components/GenericList/GenericList';
import Button from '~/components/CustomButton/CustomButton';
import Checkbox from '~/components/CustomCheckbox/CustomCheckbox';
import { ProjectsWidget } from '~/components/GenericForm/Widgets/ProjectsWidget';

//Elements
import Select from '~/elements/Select';

//Styles
import '~/components/Chat/ConversationSearch/ConversationSearch.css';

//Assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

const FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'selected', label: 'Selected' },
    { value: 'not_selected', label: 'Not Selected' },
];

const MultiTaskSelector = ({ show, selectedTasks = [], onConfirm, onClose, taskStore, t }) => {
    const [checkedTasks, setCheckedTasks] = useState([]);
    const [currentPageTasks, setCurrentPageTasks] = useState([]);
    const [reloadKey, setReloadKey] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [selectedProjectId, setSelectedProjectId] = useState(null);

    const searchInputRef = useRef(null);
    const appliedFiltersRef = useRef({ name: '' });
    const filterTypeRef = useRef('all');
    const selectedProjectIdRef = useRef(null);
    const checkedIdSet = useMemo(() => new Set(checkedTasks.map(t => t.value)), [checkedTasks]);
    const checkedIdSetRef = useRef(checkedIdSet);
    checkedIdSetRef.current = checkedIdSet;

    useEffect(() => {
        if (show) {
            setCheckedTasks([...selectedTasks]);
            if (searchInputRef.current) searchInputRef.current.value = '';
            setFilterType('all');
            setSelectedProjectId(null);
            selectedProjectIdRef.current = null;
            appliedFiltersRef.current = { name: '' };
            filterTypeRef.current = 'all';
            setReloadKey(Date.now());
        }
    }, [show]);

    const onFilter = () => {
        appliedFiltersRef.current = { name: searchInputRef.current ? searchInputRef.current.value : '' };
        setReloadKey(Date.now());
        setCurrentPageTasks([]);
    };

    const onFilterTypeChange = (option) => {
        filterTypeRef.current = option.value;
        setFilterType(option.value);
        setCurrentPageTasks([]);
        setReloadKey(Date.now());
    };

    const onProjectChange = (value) => {
        const newId = value || null;
        selectedProjectIdRef.current = newId;
        setSelectedProjectId(newId);
        setCurrentPageTasks([]);
        setReloadKey(Date.now());
    };

    const fetchTasks = useCallback((params = {}) => {
        const currentFilterType = filterTypeRef.current;
        const currentCheckedIds = [...checkedIdSetRef.current];
        const currentProjectId = selectedProjectIdRef.current;
        return taskStore.loadLookup(currentProjectId, undefined, appliedFiltersRef.current.name, params.page || 0, false, true, currentFilterType, currentCheckedIds)
            .then(list => {
                const rows = (list || []).map(item => ({
                    id: item.id,
                    task_number: item.task_number,
                    name: item.name,
                }));
                setCurrentPageTasks(rows);
                return {
                    rows,
                    pages: taskStore.currentTasksLookup.pages || 1,
                };
            });
    }, [taskStore]);

    const isTaskChecked = useCallback((taskId) => checkedIdSetRef.current.has(taskId), []);

    const toggleTask = useCallback((task) => {
        setCheckedTasks(prev => {
            const exists = prev.some(t => t.value === task.id);
            if (exists) return prev.filter(t => t.value !== task.id);
            return [...prev, { value: task.id, label: `${task.task_number} | ${task.name}` }];
        });
    }, []);

    const isAllPageSelected = currentPageTasks.length > 0 && currentPageTasks.every(pt => checkedIdSet.has(pt.id));

    const toggleSelectAll = () => {
        const currentPageIdSet = new Set(currentPageTasks.map(pt => pt.id));
        if (isAllPageSelected) {
            setCheckedTasks(prev => prev.filter(t => !currentPageIdSet.has(t.value)));
        } else {
            setCheckedTasks(prev => {
                const prevIdSet = new Set(prev.map(t => t.value));
                const toAdd = currentPageTasks
                    .filter(pt => !prevIdSet.has(pt.id))
                    .map(pt => ({ value: pt.id, label: `${pt.task_number} | ${pt.name}` }));
                return [...prev, ...toAdd];
            });
        }
    };

    const columns = useMemo(() => [
        {
            Header: `${t('Task')} #`,
            id: 'task_number',
            accessor: 'task_number',
            textAlign: 'start',
            Cell: ({ row }) => <div className="text-start">{row.original.task_number}</div>,
        },
        {
            Header: t('Task Name'),
            id: 'name',
            accessor: 'name',
            textAlign: 'start',
            Cell: ({ row }) => <div className="text-start">{row.original.name}</div>,
        },
        {
            Header: t('Select'),
            id: 'select',
            textAlign: 'center',
            accessor: 'id',
            Cell: ({ row }) => (
                <Checkbox
                    inlineBlock
                    isChecked={isTaskChecked(row.original.id)}
                    onChange={() => toggleTask(row.original)}
                    wrapperClassName="checkbox-position-fix"
                    labelClassName="ps-0"
                />
            ),
        },
    ], [t, isTaskChecked, toggleTask]);

    const filterOptions = FILTER_OPTIONS.map(opt => ({ ...opt, label: t(opt.label) }));
    const selectedFilterOption = filterOptions.find(opt => opt.value === filterType);

    return (
        <Modal backdrop="static" scrollable show={show} onHide={onClose} size="lg"
            className="stacked-modal-1060-zindex"
            backdropClassName="stacked-modal-backdrop-1060-zindex"
        >
            <Modal.Header>
                <Modal.Title className="w-100 text-center m-0">{t('Select Tasks')}</Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" alt="close_button" onClick={onClose} />
            </Modal.Header>
            <Modal.Body className="px-3 pt-2 pb-2">
                <Row className="align-items-center mb-2 g-2">
                    <Col>
                        <div className="conversation-search">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="conversation-search-input"
                                placeholder={t('Search Tasks')}
                                onKeyDown={(e) => { if (e.key === 'Enter') onFilter(); }}
                                defaultValue=""
                            />
                            <i className="fa fa-search conversation-search-icon" onClick={onFilter} />
                        </div>
                    </Col>
                    <Col sm={3}>
                        <ProjectsWidget
                            value={selectedProjectId || ''}
                            onChange={onProjectChange}
                            allowAll={true}
                            allLabel="All Projects"
                        />
                    </Col>
                    <Col xs="auto">
                        <Select
                            options={filterOptions}
                            value={selectedFilterOption}
                            onChange={onFilterTypeChange}
                            isSearchable={false}
                            style={{ minWidth: 150 }}
                        />
                    </Col>
                    <Col xs="auto" className="ms-auto">
                        <Checkbox
                            inlineBlock
                            isChecked={isAllPageSelected}
                            onChange={toggleSelectAll}
                            disabled={currentPageTasks.length === 0}
                            label={t('Select All')}
                        />
                    </Col>
                    <Col xs="auto">
                        <Button fill onClick={() => onConfirm(checkedTasks)}>
                            {t('Select')} ({checkedTasks.length})
                        </Button>
                    </Col>
                </Row>
                <div className="table-list mb-3">
                    {reloadKey && (
                        <GenericList
                            key={reloadKey}
                            columns={columns}
                            requestData={fetchTasks}
                            filters={appliedFiltersRef.current}
                            page={0}
                        />
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default inject('taskStore')(withLocalization(observer(MultiTaskSelector)));
